import React from "react";
import { useNavigate } from "react-router";
import apiAgent from "@/app/api/apiAgent";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/app/common/common-components/ui/Dialog";
import { Button } from "@/app/common/common-components/ui/Button";
import { Input } from "@/app/common/common-components/ui/Input";
import { Loader2, Search } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "@uidotdev/usehooks";

interface NewDealDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SearchResult {
  id: string;
  name: string;
  typeIcon: string;
}

const NewDealDialog: React.FC<NewDealDialogProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = React.useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 1000);

  const {
    data: searchResults,
    isLoading,
    isPending,
  } = useQuery<SearchResult[]>({
    queryKey: ["propertySearch", debouncedSearchTerm],
    queryFn: async () => {
      const response = await apiAgent.Deal.searchPropertiesAjax(debouncedSearchTerm);
      return response.data || [];
    },
    enabled: !!debouncedSearchTerm,
  });

  const handleSelectProperty = (id: string, name: string) => {
    localStorage.setItem("dealSideMenuLink", "Deals");
    navigate(`/deal/saveDeal?ID=&propertyID=${id}`);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="tw-sm:tw-max-w-md">
        <DialogHeader>
          <DialogTitle>Search listing</DialogTitle>
        </DialogHeader>

        <div className="tw-flex tw-flex-col tw-gap-4">
          <div className="tw-flex tw-flex-col tw-gap-2">
            <label htmlFor="search-input">Search by street name, complex name, or webref</label>
            <div className="tw-relative">
              <Input
                id="search-input"
                type="text"
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                placeholder="Enter search term..."
              />
              <div className="tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-pr-3">
                {isLoading ? <Loader2 className="tw-h-4 tw-w-4 tw-animate-spin tw-text-gray-400" /> : <Search className="tw-h-4 tw-w-4 tw-text-gray-400" />}
              </div>
            </div>
          </div>

          <div className="tw-max-h-60 tw-overflow-y-auto">
            {!!searchResults && searchResults?.length > 0 ? (
              <ul className="tw-divide-y tw-divide-gray-200">
                {searchResults?.map((item: SearchResult) => (
                  <li key={`SR${item.id}`} className="tw-py-2">
                    <button
                      onClick={() => handleSelectProperty(item.id, item.name)}
                      className="tw-flex tw-w-full tw-items-center tw-rounded-md tw-px-2 tw-py-1 tw-text-left hover:tw-bg-gray-50"
                    >
                      <i className={`fa ${item.typeIcon} tw-mr-2 tw-text-gray-500`}></i>
                      <span>{item.name}</span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : searchTerm && !isLoading && !isPending ? (
              <p className="tw-py-4 tw-text-center tw-text-gray-500">No results found</p>
            ) : null}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewDealDialog;
